import React, { useEffect, useState } from "react";
import { CheckboxIcon } from "@aureskonnect/react-ui";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";
import { logger } from "@helpers/logger";

import { store } from "../../../components/VerticalLayout/store";
import {
    store as peripheralStore,
    resetAllPeripherals,
    setInAllPrinters,
} from "@pages/Applications/DeviceAssociateModal/store";
import {
    store as pathStore,
    setIsActionsClicked,
    setIsEdited,
    setActions,
    setValidationAction,
} from "./store";
import { clearMultiCustomSelectionData } from "@components/Common/MultiCustomSelectV2/store";

import { verifyIfPeripheralAssociate } from "../../../helpers/peripheralsHelpers";
import { redisPublishPeripheral } from "../../../helpers/peripheralApi";
import { IpResourceEntry, UpdateResourceIp } from "@helpers/general";
import { computePrinterDuplicateIp, PrinterEditedData } from "./printerIpCheck";
import { isDesignationUnique } from "./printerUtils";
import {
    buildTicketTypeChangePrint,
    buildPrinterAddPayload,
    buildPrinterEditApplicationPayload,
    buildPrinterEditPayload,
} from "./printerDataBuilder";
import ErrorToast from "@components/Common/ErrorTost";
import { useCallbackPrompt } from "@hooks/useCallbackPrompt";
import { SPEEDS } from "../../../constants";
import { PERIPHERAL_TYPE_PRINTER } from "@constants/index";
import { useFetchGlobal } from "../../../api/hooks";

interface ModeOfSaleEntry {
    "Mode of sale": string;
    support_vente: string[];
    [key: string]: unknown;
}

export interface ModeOfSaleResponse {
    data?: ModeOfSaleEntry[];
    [key: string]: unknown;
}

export interface ApiMutationResponse {
    error?: boolean;
    [key: string]: unknown;
}

type PrintersType = {
    setIsAddPrinterClicked: Function;
    setDataIsUpdated: Function;
    isAddPrinterClicked: boolean;
    editedData: PrinterEditedData;
    setEditedData: Function;
    isAddNewPrinterButtonClicked: boolean;
    shopIdPrinter: string;
    localShopId?: any;
    inputDesignation: boolean;
    setInputDesignation: Function;
    inputIp: boolean;
    setInputIp: Function;
    dataIsUpdated?: boolean | number;
    dataTable?: any;
    setIsUpdateOperation: Function;
    isConsult: boolean;
    setIsConsult: Function;
    isComingFromAssociate?: boolean;
};

export function usePrinterConfig({
    setIsAddPrinterClicked,
    isAddPrinterClicked,
    setDataIsUpdated,
    editedData,
    setEditedData,
    isAddNewPrinterButtonClicked,
    shopIdPrinter,
    localShopId,
    inputDesignation,
    setInputDesignation,
    inputIp,
    setInputIp,
    dataIsUpdated,
    dataTable,
    setIsUpdateOperation,
    isConsult,
    setIsConsult,
    isComingFromAssociate,
}: PrintersType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const {
        userID,
        franchiseID,
        shopID,
        oneShop,
        resourceIp,
        operatorID,
    } = useSnapshot(store);
    const {
        selected,
        path,
        isActionsClicked,
        isEdited,
        actions,
        validationAction,
    } = useSnapshot(pathStore);
    const { peripherals } = useSnapshot(peripheralStore);

    const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
        isEdited
    );

    let ips: string[] = [];
    const localNamesPrinters: string[] = [];
    const pathname = window.location.pathname.toString();

    const [printerType, setPrinterType] = useState<string>(
        editedData.type !== undefined ? editedData.type : "ip"
    );
    const [selectedShopPrinterCodes, setSelectedShopPrinterCodes] = useState<
        any
    >([]);
    const [printerPath, setPrinterPath] = useState<string>("");
    const [designation, setDesignation] = useState<string>("");
    const [mark, setMark] = useState<string>("");
    const [remark, setRemark] = useState<string>("");
    const [speed, setSpeed] = useState<string | number>(19200);
    const [ip, setIp] = useState<string>("");
    const [, setPortNum] = useState<string>(
        printerType === "series" ? "COM 2" : "LPT 2"
    );
    const [fontSize, setFontSize] = useState<string | number>(10);
    const [ticketWidth, setTicketWidth] = useState<string | number>(40);
    const [ipPortNumber, setIpPortNumber] = useState<string>("7000");
    const [printerCode, setPrinterCode] = useState<any>(1);
    const [portSeries, setPortSeries] = useState<string>("COM 2");
    const [openDrawer, setOpenDrawer] = useState<boolean>(false);
    const [tickets, setTickets] = React.useState<any>(null);
    const [ticketsList, setTicketsList] = React.useState<any[]>([]);
    const [inputTicket, setInputTicket] = React.useState<boolean>(false);
    const [isWaiting, setIsWaiting] = React.useState<boolean>(false);
    const [
        isPrintersPathsModalOpened,
        setIsPrintersPathsModalOpened,
    ] = useState<boolean>(false);
    const [exist, setExist] = useState({
        isNameExist: false,
        isIpExist: false,
    });
    const [emptyIp, setEmptyIp] = React.useState<boolean>(false);
    const [canValidate, setCanValidate] = React.useState<boolean>(true);
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [codeIsExist, setCodeIsExist] = React.useState<boolean>(false);
    const [isCodeValid, setIsCodeValid] = useState(true);
    const [salesModes, setSalesModes] = React.useState<any>([]);

    const isUsbPrinter = printerType === "usb";
    const isIpPrinter = printerType === "ip";
    const appId = "KIOSK";
    let ifPeripheralAssociateObject: {
        isPeripheralAssociate: boolean;
        appId_children: any[];
    };
    const uuidUser = localStorage.getItem("uuidUser");

    // --- Arrays for dropdowns ---
    const fontSizeArray: { label: number; value: number }[] = [
        ...Array(16).keys(),
    ].map((e: any, index: number) => ({
        label: index + 1,
        value: index + 1,
    }));

    const ticketWidthArray: { label: number; value: number }[] = [
        ...Array(7).keys(),
    ].map((e: any, index: number) => ({
        label: index + 34,
        value: index + 34,
    }));

    const portNumArray: { label: string; value: string }[] = [
        ...Array(32).keys(),
    ].map((e: any, index: number) => {
        if (printerType === "series")
            return {
                label: `COM ${index + 1}`,
                value: `COM ${index + 1}`,
            };
        return { label: `LPT ${index + 1}`, value: `LPT ${index + 1}` };
    });

    const printerSpeedArray: { label: number; value: number }[] = SPEEDS.map(
        (element: number) => ({
            label: element,
            value: element,
        })
    );

    // --- Functions ---
    const fetchDataIp_Name = () => {
        let printerData: any = [];
        if (dataTable.data !== undefined) {
            if (dataTable !== undefined && dataTable.data.length > 0) {
                if (dataTable.data[0][t("Shop")] !== undefined) {
                    if (localShopId.length > 0) {
                        localShopId.forEach((element: any) => {
                            printerData = [
                                ...printerData,
                                ...dataTable.data.find((row: any) => {
                                    return row.shopId === element;
                                })?.subRows,
                            ];
                        });
                    }
                } else if (dataTable.data[0][t("Shop")] === undefined) {
                    printerData = dataTable.data;
                }
            }
        }

        printerData.forEach((datum: any) => {
            localNamesPrinters.push(
                datum[t("Printer name")]?.toUpperCase().trim()
            );
        });
        ips = resourceIp
            .filter((x: IpResourceEntry) => {
                // Printers can legitimately share an IP (same network
                // printer declared multiple times with different
                // queues / roles). Exclude them so the duplicate-IP
                // check only fires for other peripheral types.
                if (x.type === PERIPHERAL_TYPE_PRINTER) return false;
                return x.shopId === localShopId[0] && Boolean(x.ip);
            })
            .map((x: IpResourceEntry) => x.ip);
    };

    const fetchModeOfSale = React.useCallback(async () => {
        const languageHeader = "en-US";
        const headers = new Headers({
            "Accept-Language": languageHeader,
        });
        const requestOptions = {
            method: "GET",
            headers: headers,
        };
        try {
            const response = (await fetchGlobal(
                `${process.env.REACT_APP_API_V2_URL}/settings/general/mode_of_sale?userId=${userID}&shopId=${shopID}&franchiseId=${franchiseID}`,
                requestOptions
            )) as ModeOfSaleResponse;

            const saleModes: any[] = (response?.data ?? []).map((saleMode) => {
                return {
                    // eslint-disable-next-line array-callback-return
                    saleSupports: saleMode.support_vente.map((el: any) => {
                        if (el === "KIOSK")
                            return {
                                designation: saleMode["Mode of sale"],
                                state: false,
                            };
                    }),
                };
            });
            let rowDataSales: any[] = [];

            let salesSupportMode: any[] = [];
            if (
                editedData["customer_ticket"]?.customerTicketSalesModes !==
                undefined
            ) {
                for (let element of editedData["customer_ticket"]
                    ?.customerTicketSalesModes) {
                    rowDataSales.push(element);
                }
            }
            saleModes !== undefined &&
                saleModes.forEach((el: any) => {
                    el.saleSupports.forEach((elt: any) => {
                        if (elt !== undefined) {
                            salesSupportMode.push(elt.designation);
                            rowDataSales = rowDataSales.filter(
                                (o) => o.designation !== elt.designation
                            );
                        }
                    });
                });

            let sales: any[] = [];
            let salesModes: any[] = [];
            saleModes !== undefined &&
                saleModes.forEach((el: any) => {
                    if (
                        editedData["customer_ticket"]
                            ?.customerTicketSalesModes === undefined ||
                        editedData["customer_ticket"]?.customerTicketSalesModes
                            .length === 0
                    ) {
                        el.saleSupports.forEach((elt: any) => {
                            if (
                                editedData["customer_ticket"]
                                    ?.customerTicketSalesModes === undefined ||
                                editedData["customer_ticket"]
                                    ?.customerTicketSalesModes.length === 0
                            ) {
                                elt !== undefined && sales.push(elt);
                            }
                        });
                    } else {
                        for (let element of editedData["customer_ticket"]
                            ?.customerTicketSalesModes) {
                            salesModes.push(element.designation);
                            sales.push(element);
                        }
                        el.saleSupports.forEach((elt: any) => {
                            if (
                                elt !== undefined &&
                                !salesModes.includes(elt.designation)
                            ) {
                                sales.push(elt);
                            }
                        });
                    }
                });
            sales = [...new Set(sales)];
            for (let sale of rowDataSales) {
                sales = sales.filter((o) => o.designation !== sale.designation);
            }

            setSalesModes(sales);
        } catch (error) {
            logger.error("fetchModeOfSale failed:", error);
        }
        // eslint-disable-next-line
    }, [franchiseID, shopID, userID, editedData]);

    function uniqueDesignationIp(data: any, type: string) {
        switch (type) {
            case "designation":
                if (
                    (localNamesPrinters.includes(data.toUpperCase().trim()) &&
                        editedData.Id === undefined) ||
                    (localNamesPrinters.includes(data.toUpperCase().trim()) &&
                        editedData.Id !== undefined &&
                        editedData[t("Printer name")].toUpperCase().trim() !==
                            data.toUpperCase())
                ) {
                    setExist((prevState: any) => ({
                        ...prevState,
                        isNameExist: true,
                    }));
                } else {
                    setExist((prevState: any) => ({
                        ...prevState,
                        isNameExist: false,
                    }));
                }
                break;
            case "ip":
                if (
                    (ips.includes(data) && editedData.Id === undefined) ||
                    (ips.includes(data) &&
                        editedData.Id !== undefined &&
                        editedData[t(`IP/port`)] !== data)
                ) {
                    setExist((prevState: any) => ({
                        ...prevState,
                        isIpExist: true,
                    }));
                } else {
                    setExist((prevState: any) => ({
                        ...prevState,
                        isIpExist: false,
                    }));
                }
                break;
        }
    }

    async function RedisPublishPeripheral() {
        const ids =
            localShopId.length !== 0
                ? localShopId
                : oneShop
                ? [shopID.toString()]
                : [shopIdPrinter.toString()];
        await redisPublishPeripheral(ids as string[], fetchGlobal);
    }

    const activateCheckbox = () => {
        let checks = document.getElementsByClassName("PrivateSwitchBase-input");
        for (let i = 0; i < checks.length; i++) {
            checks[i].classList.remove("not-allowed-icon-check__clz");
        }
    };

    function IsExistPeripheral(data: any, des: string) {
        return isDesignationUnique(data, des, t("Printer name"));
    }

    async function handleEditButtonClickEvent() {
        toast.dismiss();
        resetAllPeripherals();
        clearMultiCustomSelectionData();

        if (canValidate) {
            let dataPrint: any;

            setCanValidate(false);
            let userId: number;
            let shopId: string;
            let franchiseId: string;
            userId = userID;
            shopId = oneShop ? shopID.toString() : shopIdPrinter.toString();
            franchiseId = franchiseID;

            ifPeripheralAssociateObject = await verifyIfPeripheralAssociate(
                editedData["Id"]!,
                shopId,
                franchiseID,
                userID,
                appId,
                fetchGlobal
            );

            let apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/peripheral/modify`;
            let data = [
                { label: "Customer", value: "01" },
                { label: "Production", value: "02" },
                {
                    label: "Sticker",
                    value: "03",
                },
                {
                    label: t("Voucher"),
                    value: "04",
                },
            ];

            if (
                editedData["tickets"].value === "01" &&
                (tickets.value === "02" ||
                    tickets.value === "04" ||
                    tickets.value === "03")
            ) {
                dataPrint = buildTicketTypeChangePrint(salesModes, true, false);
            }
            if (
                (editedData["tickets"].value === "02" ||
                    editedData["tickets"].value === "04" ||
                    editedData["tickets"].value === "03") &&
                tickets.value === "01"
            ) {
                dataPrint = buildTicketTypeChangePrint(salesModes, false, true);
            }

            let originTickets = data.filter(
                (el: any) => tickets.value === el.value
            );

            const peripheral =
                peripherals.find(
                    (item: any) => item.peripheralId === editedData.Id
                ) || {};

            const fields = {
                designation,
                mark,
                remark,
                printerType,
                speed,
                printerPath,
                ip,
                ipPortNumber,
                fontSize,
                ticketWidth,
                portSeries,
                printerCode,
                openDrawer,
            };
            const commonParams = {
                userId,
                shopId,
                franchiseId,
                operatorId: operatorID,
                uuidUser,
            };

            let savedDataApplication = buildPrinterEditApplicationPayload({
                ...commonParams,
                editedData,
                fields,
                originTicket: originTickets[0],
                peripheral,
            });

            const isSameTicketValue =
                tickets.value === editedData["tickets"].value;

            let savedData = buildPrinterEditPayload({
                ...commonParams,
                editedData,
                fields,
                originTicket: originTickets[0],
                isSameTicketValue,
                dataPrint,
                application: savedDataApplication,
            });

            setIsWaiting(true);
            try {
                const data = (await fetchGlobal(apiUrl, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(savedData),
                    method: "PUT",
                })) as ApiMutationResponse;
                mutate(apiUrl, data);
                if (data.error) {
                    setCanValidate(true);
                    throw Error("Error!");
                }
                setCanValidate(true);
                toast.success(`${t("The device is successfully changed")!}`, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                });
                setInAllPrinters(
                    {
                        ...savedData.data[0],
                        Id: savedData.data[0].id,
                    },
                    true
                );
                if (ifPeripheralAssociateObject.isPeripheralAssociate)
                    setTimeout(RedisPublishPeripheral, 3000);
                setIsAddPrinterClicked(!isAddPrinterClicked);
                setDataIsUpdated(true);
                UpdateResourceIp(userID, franchiseID, fetchGlobal);
                setEditedData([]);
                setIsEdited(false);
                setIsAddPrinterClicked(false);
                activateCheckbox();
                setTickets({ label: t("Customer"), value: "01" });
                setIsWaiting(false);
            } catch (e: any) {
                setCanValidate(true);
                setIsWaiting(false);
                toast.error(`${t("There's an error")!}`, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
        }
    }

    async function handleValidateButtonClickEvent() {
        let hasIpConflict: number = 0;
        toast.dismiss();
        resetAllPeripherals();
        clearMultiCustomSelectionData();
        let apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/printer`;
        let userId: number;
        let franchiseId: string;
        if (oneShop) {
            localShopId = [shopID.toString()];
        }

        if (localShopId.length === 0) {
            ErrorToast(`${t("Attention ! you must select a store.")!}`);
        } else if (localShopId.length >= 1) {
            setIsWaiting(true);
            if (canValidate) {
                setCanValidate(false);
                try {
                    let action: boolean = false;
                    let localApiCalls: Promise<any>[] = [];
                    for (let index = 0; index < localShopId.length; index++) {
                        const element = localShopId[index];
                        userId = userID;
                        franchiseId = franchiseID;
                        let data = [
                            { label: "Customer", value: "01" },
                            { label: "Production", value: "02" },
                            {
                                label: "Sticker",
                                value: "03",
                            },
                            {
                                label: t("Voucher"),
                                value: "04",
                            },
                        ];

                        let originTickets = data.filter(
                            (el: any) => tickets.value === el.value
                        );
                        let savedData = buildPrinterAddPayload({
                            userId,
                            shopId: element,
                            franchiseId,
                            operatorId: operatorID,
                            uuidUser,
                            fields: {
                                designation,
                                mark,
                                remark,
                                printerType,
                                speed,
                                printerPath,
                                ip,
                                ipPortNumber,
                                fontSize,
                                ticketWidth,
                                portSeries,
                                printerCode,
                                openDrawer,
                            },
                            originTicket: originTickets[0],
                            salesModes,
                        });

                        let filteredData = JSON.parse(
                            JSON.stringify(resourceIp)
                        )
                            ?.filter(
                                (item: any) =>
                                    item?.shopId === element &&
                                    item?.ip?.trim() !== "" &&
                                    item?.type !== PERIPHERAL_TYPE_PRINTER
                            )
                            .map((y: any) => {
                                return y.ip;
                            });
                        filteredData = [...new Set(filteredData)];

                        let dataSearch: any = [];
                        if (dataTable.data.length > 0) {
                            dataSearch =
                                oneShop ||
                                dataTable.data[0][t("Shop")] === undefined
                                    ? dataTable.data
                                    : dataTable.data.find((x: any) => {
                                          return x.shopId === element;
                                      }).subRows;
                        }
                        if (filteredData.includes(ip)) {
                            hasIpConflict++;
                        }

                        if (IsExistPeripheral(dataSearch, designation.trim())) {
                            if (!filteredData.includes(ip)) {
                                action = true;
                                localApiCalls.push(
                                    fetchGlobal(apiUrl, {
                                        headers: {
                                            "Content-Type": "application/json",
                                        },
                                        body: JSON.stringify(savedData),
                                        method: "POST",
                                    })
                                );
                            }
                        }
                    }
                    const results = await Promise.all(localApiCalls);
                    for (const data of results) {
                        mutate(apiUrl, data);
                        if (data.error) {
                            throw Error("Error while saving selection!");
                        }
                    }
                    if (action === true) {
                        toast.success(
                            `${t("The device is successfully added")!}`,
                            {
                                position: toast.POSITION.TOP_CENTER,
                                autoClose: 2000,
                                theme: "colored",
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true,
                                icon: (
                                    <CheckboxIcon
                                        height={25}
                                        width={25}
                                        fill="white"
                                    />
                                ),
                            }
                        );
                    }
                    if (hasIpConflict > 0) {
                        setCanValidate(true);
                        ErrorToast(
                            t(
                                "Please note, the device could not be added to one or more stores. Please check the IP address settings."
                            )
                        );
                    }
                    setTickets({ label: t("Customer"), value: "01" });
                    setDataIsUpdated(true);
                    UpdateResourceIp(userID, franchiseID, fetchGlobal);
                    setEditedData([]);
                    setIsEdited(false);
                    activateCheckbox();
                    setCanValidate(true);
                    setIsWaiting(false);
                } catch (e: any) {
                    setCanValidate(true);
                    setIsWaiting(false);
                    toast.error(`${t("There's an error")}!`, {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                        theme: "colored",
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                }
            }
        }
    }

    // --- useEffects ---
    React.useEffect(() => {
        fetchDataIp_Name();
        uniqueDesignationIp(designation, "designation");
        // eslint-disable-next-line
    }, [dataIsUpdated, localShopId, designation]);

    React.useEffect(() => {
        if (isAddPrinterClicked) fetchModeOfSale();
    }, [fetchModeOfSale, isAddPrinterClicked]);

    React.useEffect(() => {
        oneShop || isComingFromAssociate !== undefined
            ? setSelectedShopPrinterCodes(
                  dataTable?.data?.map((el: any) => el.code) || []
              )
            : setSelectedShopPrinterCodes(
                  dataTable.data
                      .filter((el: any) => {
                          return localShopId.includes(el.shopId);
                      })
                      .map((element: any) => element.subRows)
                      .reduce(function (acc: any, curr: any) {
                          return acc.concat(curr);
                      }, [])
                      .map((el: any) => el.code)
              );

        setTicketsList([
            { label: t("Customer"), value: "01" },
            { label: t("Production"), value: "02" },
            {
                label: t("Sticker"),
                value: "03",
            },
            {
                label: t("Voucher"),
                value: "04",
            },
        ]);
    }, [localShopId, dataTable.data, isComingFromAssociate, oneShop, t]);

    useEffect(() => {
        if (editedData.length !== 0) {
            setPrinterType(editedData.type!);
            setDesignation(
                editedData[t(`Printer name`)]
                    ? editedData[t(`Printer name`)]
                    : ""
            );
            setPrinterPath(editedData.path);
            setIp(editedData.type === "ip" ? editedData[t(`IP/port`)] : "");
            setPortNum(
                editedData.type === "series"
                    ? editedData["port"]
                    : editedData.type !== "ip"
                    ? editedData[t(`IP/port`)]
                    : printerType === "series"
                    ? "COM 2"
                    : "LPT 2"
            );

            setSpeed(editedData["speed"] !== "" ? editedData["speed"] : 19200);
            setFontSize(editedData[t(`Font size`)]);
            setTicketWidth(editedData["ticket_width"]);
            setMark(editedData.Marque);
            setRemark(editedData.Remarque);
            setPortSeries(editedData.port_series);
            setIpPortNumber(editedData["port"]);
            setInputDesignation(false);
            setInputTicket(false);
            setInputIp(false);
            setEmptyIp(false);

            setTickets(
                editedData.tickets === undefined ||
                    editedData.tickets === "" ||
                    editedData.tickets.length === 0
                    ? null
                    : {
                          label: t(editedData.tickets.label),
                          value: editedData.tickets.value,
                      }
            );
            setOpenDrawer(editedData.open_drawer === true);
        } else {
            setPrinterType("ip");
            setDesignation("");
            setSpeed(19200);
            setMark("");
            setIp("");
            setRemark("");
            setFontSize(9);
            setPrinterPath("");
            setTicketWidth(40);
            setPortNum(printerType === "series" ? "COM 2" : "LPT 2");
            setIpPortNumber("7000");
            setPortSeries("COM 2");
            setInputDesignation(false);
            setInputTicket(false);
            setInputIp(false);
            setEmptyIp(false);
            setTickets({ label: t("Customer"), value: "01" });
            setOpenDrawer(false);

            setExist((prevState: any) => ({
                ...prevState,
                isIpExist: false,
            }));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editedData, isAddNewPrinterButtonClicked, t]);

    useEffect(() => {
        if (editedData.length === 0) {
            setPrinterCode(
                selectedShopPrinterCodes.length > 0
                    ? Math.max(...selectedShopPrinterCodes) + 1
                    : 1
            );
        }
        // eslint-disable-next-line
    }, [selectedShopPrinterCodes.length]);

    useEffect(() => {
        setExist((prev: any) => ({
            ...prev,
            isIpExist: computePrinterDuplicateIp(
                ip,
                editedData,
                localShopId,
                resourceIp as IpResourceEntry[]
            ),
        }));
    }, [editedData, ip, localShopId, resourceIp]);

    useEffect(() => {
        if (editedData.length !== 0) {
            setPrinterCode(editedData.code !== undefined ? editedData.code : 0);
        }
    }, [editedData]);

    useEffect(() => {
        if (printerType !== "ip") {
            setEmptyIp(false);
        }
        if (printerType === "ip" && isEdited === false) {
            setEmptyIp(false);
            setExist((prevState: any) => ({
                ...prevState,
                isIpExist: false,
            }));
        }
        if (ip.trim() === "") {
            setExist((prevState: any) => ({
                ...prevState,
                isIpExist: false,
            }));
        }
        // 1032;
    }, [printerType, editedData, ip, isEdited]);

    useEffect(() => {
        if (editedData.length !== 0 && editedData?.Id !== undefined) {
            setIsUpdateOperation(true);
        } else {
            setIsUpdateOperation(false);
        }

        if (editedData.length === 0) {
            (oneShop &&
                selectedShopPrinterCodes
                    .map((el: any) => Number(el))
                    .includes(Number(printerCode))) ||
            (!oneShop &&
                selectedShopPrinterCodes
                    .map((el: any) => Number(el))
                    .includes(Number(printerCode)))
                ? setCodeIsExist(true)
                : setCodeIsExist(false);
        } else {
            let localSelectedShopPrinterCodes =
                oneShop || isComingFromAssociate
                    ? dataTable.data
                          .map((el: any) => el.code)
                          .filter(
                              (elem: any) =>
                                  Number(elem) !== Number(editedData.code)
                          )
                    : dataTable.data
                          .filter((el: any) => {
                              return editedData.shopId === el.shopId;
                          })
                          .map((element: any) => element.subRows)
                          .reduce(function (acc: any, curr: any) {
                              return acc.concat(curr);
                          }, [])
                          .map((el: any) => el.code)
                          .filter((elem: any) => elem !== editedData.code);
            localSelectedShopPrinterCodes
                .map((el: any) => Number(el))
                .includes(Number(printerCode))
                ? setCodeIsExist(true)
                : setCodeIsExist(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editedData, localShopId, printerCode, selectedShopPrinterCodes]);

    useEffect(() => {
        if (editedData.length !== 0) {
            setPortNum(
                editedData.type === "series"
                    ? editedData["port"]
                    : editedData.type !== "ip"
                    ? editedData[t(`IP/port`)]
                    : printerType === "series"
                    ? "COM 2"
                    : "LPT 2"
            );
            setSpeed(editedData["speed"] !== "" ? editedData["speed"] : 19200);

            setIpPortNumber(editedData["port"]);
        } else {
            setSpeed(19200);
            setPortNum(printerType === "series" ? "COM 2" : "LPT 2");
            setIpPortNumber("7000");
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [printerType]);

    useEffect(() => {
        if (path !== "" && selected !== "") {
            setPrinterPath(selected);
            setIsEdited(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path, selected]);

    React.useEffect(() => {
        setActions(confirmNavigation);
        setIsActionsClicked(showPrompt as boolean);
        setValidationAction(cancelNavigation);
    }, [showPrompt, confirmNavigation, cancelNavigation]);

    // Call fetchDataIp_Name on each render (same as original)
    fetchDataIp_Name();

    return {
        t,
        // State values
        printerType,
        setPrinterType,
        designation,
        setDesignation,
        mark,
        setMark,
        remark,
        setRemark,
        speed,
        setSpeed,
        ip,
        setIp,
        fontSize,
        setFontSize,
        ticketWidth,
        setTicketWidth,
        ipPortNumber,
        setIpPortNumber,
        printerCode,
        setPrinterCode,
        portSeries,
        setPortSeries,
        openDrawer,
        setOpenDrawer,
        tickets,
        setTickets,
        ticketsList,
        inputTicket,
        setInputTicket,
        isWaiting,
        isPrintersPathsModalOpened,
        setIsPrintersPathsModalOpened,
        exist,
        emptyIp,
        setEmptyIp,
        isModalOpened,
        setIsModalOpened,
        codeIsExist,
        isCodeValid,
        setIsCodeValid,
        printerPath,
        setPrinterPath,
        // Computed values
        isUsbPrinter,
        isIpPrinter,
        pathname,
        // Arrays
        fontSizeArray,
        ticketWidthArray,
        portNumArray,
        printerSpeedArray,
        // Store values
        isActionsClicked,
        isEdited,
        actions,
        validationAction,
        // Functions
        uniqueDesignationIp,
        activateCheckbox,
        handleEditButtonClickEvent,
        handleValidateButtonClickEvent,
        // Store actions
        setIsActionsClicked,
        setActions,
        setIsEdited,
        resetAllPeripherals,
        clearMultiCustomSelectionData,
    };
}
